import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { findMySquad } from '../services/squadApi';
import { trackPageView, trackFormSubmission, trackFormError, trackEvent } from '../utils/analytics';
import Navigation from './Navigation';
import Footer from './Footer';

export default function FindMySquadPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [eventId, setEventId] = useState(searchParams.get('event_id') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Track page view
  useEffect(() => {
    trackPageView('Find My Squad', {
      has_email_param: !!email,
      has_event_id_param: !!eventId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    trackFormSubmission('Find My Squad', {
      has_event_id: !!eventId,
    });

    try {
      const result = await findMySquad({ email, eventId });
      
      if (result && result.squadId) {
        // Convert Ticket Tailor event_id to UUID if needed
        let eventUuid = result.eventId;
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(eventUuid)) {
          // Convert to UUID using Web Crypto API (browser-compatible)
          try {
            const encoder = new TextEncoder();
            const data = encoder.encode(eventUuid);
            const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            eventUuid = `${hashHex.substring(0, 8)}-${hashHex.substring(8, 12)}-${hashHex.substring(12, 16)}-${hashHex.substring(16, 20)}-${hashHex.substring(20, 32)}`;
          } catch (err) {
            console.error('Error converting event ID to UUID:', err);
            // Fallback: use the eventId as-is if crypto fails
          }
        }
        
        trackEvent('Squad Found', {
          event_id: eventUuid,
          squad_id: result.squadId,
          had_event_id_param: !!eventId,
        });
        
        navigate(`/e/${eventUuid}/s/${result.squadId}`);
      } else {
        setError('No squad found for this email/event. Make sure you have completed a purchase.');
        trackFormError('Find My Squad', 'squad_not_found');
        trackEvent('Squad Not Found', {
          had_event_id: !!eventId,
        });
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
      trackFormError('Find My Squad', 'api_error');
      trackEvent('Find My Squad - Error', {
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] font-inter">
      <Navigation />
      <div className="flex min-h-[calc(100vh-200px)] items-center justify-center px-4 py-16">
        <main className="w-full max-w-md rounded-lg bg-gray-900 p-8 shadow-lg border border-gray-800">
          <h1 className="mb-6 text-2xl font-bold text-white">
            Find Your Squad
          </h1>
          
          <p className="mb-6 text-gray-300">
            Enter your email address to find and join your squad for the event.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-500 focus:border-[#4ADE80] focus:outline-none focus:ring-2 focus:ring-[#4ADE80]"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label
                htmlFor="event_id"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Event ID (optional)
              </label>
              <input
                type="text"
                id="event_id"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                className="w-full rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-500 focus:border-[#4ADE80] focus:outline-none focus:ring-2 focus:ring-[#4ADE80]"
                placeholder="ev_7526688 (optional)"
              />
              <p className="mt-1 text-xs text-gray-500">
                Only needed if you purchased tickets for multiple events
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-red-900/20 border border-red-800/50 p-3 text-sm text-red-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !email}
              className="w-full rounded-md bg-[#4ADE80] px-4 py-2 font-medium text-[#050505] hover:bg-[#3bc96a] focus:outline-none focus:ring-2 focus:ring-[#4ADE80] focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Finding your squad...' : 'Find My Squad'}
            </button>
          </form>
        </main>
      </div>
      <Footer />
    </div>
  );
}
